import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiKeysAPI } from "./api-keys.api";
import { GetApiKeysQuery, GetApiKeysResponse } from "./api-keys.types";

const queryKeys = {
  all: ["api-keys", "list"] as const,
  details: (uuid: string) => ["api-keys", "list", { uuid }] as const,
};

export const useGetApiKeys = (query?: GetApiKeysQuery) => {
  return useQuery<GetApiKeysResponse>({
    queryKey: query ? [...queryKeys.all, query] : queryKeys.all,
    queryFn: () => ApiKeysAPI.get(query),
  });
};

export const useCreateApiKey = (options?: {
  onSuccess?: (data: unknown) => void;
}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ApiKeysAPI.create,
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    // Always refetch after error or success:
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.all }),
  });

  return mutation;
};

export const useDeleteApiKey = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ApiKeysAPI.delete,
    onMutate: async (uuid) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: queryKeys.all });

      // Snapshot the previous value
      const previous = queryClient.getQueryData(queryKeys.all);

      // Optimistically update to the new value
      queryClient.setQueryData(queryKeys.all, (old: GetApiKeysResponse) => ({
        ...old,
        data: old?.data?.filter((item) => item.uuid !== uuid),
      }));

      // Return a context object with the snapshotted value
      return { previous };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (err, uuid, context) => {
      queryClient.setQueryData(queryKeys.all, context?.previous);
    },
    // Always refetch after error or success:
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.all }),
  });

  return mutation;
};
